import React, { useState, useEffect } from 'react';
import { ref, get, set, remove, push } from 'firebase/database';
import { database } from '../../firebase';

interface Test {
  id: string;
  name: string;
  exam: string;
  duration: number;
  totalMarks: number;
  negativeMarks: number;
  questions: string[];
  type: 'mock' | 'chapter' | 'paper';
  status: 'active' | 'inactive';
}

export const TestManagement: React.FC = () => {
  const [tests, setTests] = useState<Test[]>([]);
  const [questions, setQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTest, setEditingTest] = useState<Test | null>(null);

  const [newTest, setNewTest] = useState<Partial<Test>>({
    name: '',
    exam: '',
    duration: 180,
    totalMarks: 0,
    negativeMarks: 1,
    questions: [],
    type: 'mock',
    status: 'active'
  });

  useEffect(() => {
    fetchTests();
    fetchQuestions();
  }, []);

  const fetchTests = async () => {
    try {
      const testsRef = ref(database, 'tests');
      const snapshot = await get(testsRef);
      
      if (snapshot.exists()) {
        const testsData = snapshot.val();
        const testsList = Object.entries(testsData).map(([id, data]: [string, any]) => ({
          id,
          ...data
        }));
        setTests(testsList);
      }
    } catch (error) {
      console.error('Error fetching tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const questionsRef = ref(database, 'questions');
      const snapshot = await get(questionsRef);
      
      if (snapshot.exists()) {
        const questionsData = snapshot.val();
        const questionsList = Object.entries(questionsData).map(([id, data]: [string, any]) => ({
          id,
          ...data
        }));
        setQuestions(questionsList);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const saveTest = async () => {
    try {
      const testData = {
        ...newTest,
        totalMarks: (newTest.questions?.length || 0) * 4
      };

      if (editingTest) {
        const testRef = ref(database, `tests/${editingTest.id}`);
        await set(testRef, testData);
        setTests(tests.map(t => 
          t.id === editingTest.id ? { ...testData, id: editingTest.id } as Test : t
        ));
      } else {
        const testsRef = ref(database, 'tests');
        const newTestRef = push(testsRef);
        await set(newTestRef, testData);
        setTests([...tests, { ...testData, id: newTestRef.key } as Test]);
      }
      
      resetForm();
    } catch (error) {
      console.error('Error saving test:', error);
    }
  };

  const deleteTest = async (id: string) => {
    if (!confirm('Are you sure you want to delete this test?')) return;
    
    try {
      const testRef = ref(database, `tests/${id}`);
      await remove(testRef);
      setTests(tests.filter(t => t.id !== id));
    } catch (error) {
      console.error('Error deleting test:', error);
    }
  };

  const resetForm = () => {
    setNewTest({
      name: '',
      exam: '',
      duration: 180,
      totalMarks: 0,
      negativeMarks: 1,
      questions: [],
      type: 'mock',
      status: 'active'
    });
    setShowAddForm(false);
    setEditingTest(null);
  };

  const startEdit = (test: Test) => {
    setNewTest(test);
    setEditingTest(test);
    setShowAddForm(true);
  };

  const toggleQuestionSelection = (questionId: string) => {
    const currentQuestions = newTest.questions || [];
    const isSelected = currentQuestions.includes(questionId);
    
    if (isSelected) {
      setNewTest({
        ...newTest,
        questions: currentQuestions.filter(id => id !== questionId)
      });
    } else {
      setNewTest({
        ...newTest,
        questions: [...currentQuestions, questionId]
      });
    }
  };

  const TestForm = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold">
              {editingTest ? 'Edit Test' : 'Create New Test'}
            </h3>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Test Name</label>
                <input
                  type="text"
                  value={newTest.name}
                  onChange={(e) => setNewTest({...newTest, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter test name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Exam</label>
                  <select
                    value={newTest.exam}
                    onChange={(e) => setNewTest({...newTest, exam: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Exam</option>
                    <option value="NDA">NDA</option>
                    <option value="JEE Main">JEE Main</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={newTest.type}
                    onChange={(e) => setNewTest({...newTest, type: e.target.value as any})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="mock">Mock Test</option>
                    <option value="chapter">Chapter Test</option>
                    <option value="paper">Paper Test</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                  <input
                    type="number"
                    value={newTest.duration}
                    onChange={(e) => setNewTest({...newTest, duration: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Negative Marks</label>
                  <input
                    type="number"
                    value={newTest.negativeMarks}
                    onChange={(e) => setNewTest({...newTest, negativeMarks: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={newTest.status}
                  onChange={(e) => setNewTest({...newTest, status: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="pt-4">
                <p className="text-sm text-gray-600">
                  Selected Questions: {newTest.questions?.length || 0}
                </p>
                <p className="text-sm text-gray-600">
                  Total Marks: {(newTest.questions?.length || 0) * 4}
                </p>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Select Questions</h4>
              <div className="max-h-96 overflow-y-auto border border-gray-300 rounded-md p-3">
                {questions.map((question) => (
                  <div key={question.id} className="flex items-start space-x-3 mb-3 p-2 hover:bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={newTest.questions?.includes(question.id) || false}
                      onChange={() => toggleQuestionSelection(question.id)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="text-sm text-gray-900 mb-1">
                        {question.text.substring(0, 100)}...
                      </div>
                      <div className="flex space-x-2">
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">{question.exam}</span>
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">{question.subject}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              onClick={resetForm}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={saveTest}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              {editingTest ? 'Update' : 'Create'} Test
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">Test Management ({tests.length})</h3>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Create Test
        </button>
      </div>

      <div className="space-y-4">
        {tests.map((test) => (
          <div key={test.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h4 className="text-lg font-medium text-gray-900">{test.name}</h4>
                  <span className={`px-2 py-1 text-xs rounded ${
                    test.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {test.status}
                  </span>
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>📚 {test.exam}</span>
                  <span>⏱️ {test.duration} min</span>
                  <span>📝 {test.questions?.length || 0} questions</span>
                  <span>🎯 {test.totalMarks} marks</span>
                  <span>📊 {test.type}</span>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => startEdit(test)}
                  className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteTest(test.id)}
                  className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showAddForm && <TestForm />}
    </div>
  );
};